import React, { createRef, forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { classNames } from 'primereact/utils';
import { CSSTransition } from 'react-transition-group';
import { Ripple } from 'primereact/ripple';
import { Tooltip } from 'primereact/tooltip';
import { Badge } from 'primereact/badge';

const AppSubmenu = forwardRef((props, ref) => {
    const { isSubmenu = false } = props;
    const [activeIndex, setActiveIndex] = useState(null);

    const onMenuItemClick = (event, item, index) => {
        if (item.disabled) {
            event.preventDefault();
            return true;
        }

        if (props.root && props.onRootItemClick) {
            props.onRootItemClick({
                originalEvent: event,
                item: item
            });
        }

        // execute command
        if (item.command) {
            item.command({ originalEvent: event, item: item });
            event.preventDefault();
        }

        if (item.items) {
            const isCollapsable = item.isCollapsable !== false;
            if (isCollapsable) {
                setActiveIndex(activeIndex === index ? null : index);
            }
        } else {
            if (props.menuMode !== 'static') {
                const ink = getInk(event.currentTarget);
                if (ink) {
                    removeClass(ink, 'p-ink-active');
                }
            }
        }

        if (props.onMenuItemClick) {
            props.onMenuItemClick({
                originalEvent: event,
                item: item
            });
        }
    };

    const getInk = (el) => {
        for (let i = 0; i < el.children.length; i++) {
            if (typeof el.children[i].className === 'string' && el.children[i].className.indexOf('p-ink') !== -1) {
                return el.children[i];
            }
        }
        return null;
    };

    const removeClass = (element, className) => {
        if (element.classList) element.classList.remove(className);
        else element.className = element.className.replace(new RegExp('(^|\\b)' + className.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
    };

    const onMenuItemMouseEnter = (index) => {
        if (props.root && props.menuActive && isHorizontalOrSlim() && !isMobile()) {
            setActiveIndex(index);
        }
    };

    const visible = (item) => {
        return typeof item.visible === 'function' ? item.visible() : item.visible !== false;
    };

    const isHorizontalOrSlim = useCallback(() => {
        return props.menuMode === 'horizontal' || props.menuMode === 'slim';
    }, [props.menuMode]);

    const isMobile = useCallback(() => {
        return window.innerWidth < 991;
    }, []);

    const isHorizontal = () => {
        return props.menuMode === 'horizontal';
    };
    const isSlim = () => {
        return props.menuMode === 'slim';
    };

    useEffect(() => {
        if (!props.menuActive && isHorizontalOrSlim() && !isMobile()) {
            setActiveIndex(null);
        }
    }, [props.menuActive, isHorizontalOrSlim, isMobile]);

    const itemMatchesPath = (item, currentPath) => {
        if (!currentPath) {
            return false;
        }

        if (item.to && typeof item.to === "string") {
            if (currentPath === item.to || currentPath.startsWith(item.to + "/")) {
                return true;
            }
        }

        if (Array.isArray(item.items)) {
            return item.items.some((child) => itemMatchesPath(child, currentPath));
        }

        return false;
    };

    useEffect(() => {
        if (!props.currentPath || !Array.isArray(props.items)) {
            return;
        }

        const indexToActivate = props.items.findIndex(
            (item) =>
                item &&
                item.items &&
                item.isCollapsable !== false &&
                itemMatchesPath(item, props.currentPath)
        );

        if (indexToActivate !== -1 && indexToActivate !== activeIndex) {
            setActiveIndex(indexToActivate);
        }
    }, [props.currentPath]);

    const getLink = (item, index) => {
        const menuitemIconClassName = classNames('layout-menuitem-icon', item.icon);
        let _marginLeft = '';
        if (isSubmenu) {
            if (!item.icon) {
                _marginLeft = '15px';
            }
        } else {
            if (!item.icon) {
                _marginLeft = '0px';
            }
        }
        const content = (
            <>
                {item.icon && <i className={menuitemIconClassName}></i>}
                <span className="layout-menuitem-text" style={{ marginLeft: _marginLeft }}>{item.label}</span>
                {item.badge && <Badge value={item.badge} style={item.badgeStyle} className={classNames(item.badgeClassName, 'p-badge-no-gutter')} />}
                {item.items && item.isCollapsable !== false && <i className="pi pi-fw pi-angle-down layout-submenu-toggler"></i>}
                <Ripple />
            </>
        );
        const commonLinkProps = {
            style: item.style,
            className: classNames(item.className, 'p-ripple tooltip', { 'p-disabled': item.disabled, 'p-link': !item.to }),
            target: item.target,
            onClick: (e) => onMenuItemClick(e, item, index),
            onMouseEnter: () => onMenuItemMouseEnter(index)
        };

        if (item.url) {
            return (
                <a data-pr-tooltip={props.root && item.label} href={item.url} rel="noopener noreferrer" {...commonLinkProps}>
                    {content}
                </a>
            );
        } else if (item.separator) {
            return (
                <hr className="m-0" />
            )
        } else if (!item.to) {
            return (
                <a data-pr-tooltip={props.root && item.label} type="button" {...commonLinkProps}>
                    {content}
                </a>
            );
        }

        return (
            <NavLink data-pr-tooltip={props.root && item.label} to={item.to} {...commonLinkProps} className={({ isActive }) => classNames(commonLinkProps.className, isActive ? 'router-link-active' : undefined)}>
                {content}
            </NavLink>
        );
    };

    const getItems = () => {
        const transitionTimeout = props.root ? 0 : { enter: 1000, exit: 450 };
        return props.items.map((item, i) => {
            if (visible(item)) {
                const submenuRef = createRef();
                const isCollapsable = item.isCollapsable !== false;
                const active = isCollapsable ? activeIndex === i : true;
                let styleClass = classNames(item.badgeStyleClass, { 'active-menuitem': active }, { 'layout-root-menuitem': props.root });

                const menuPaddingStyle = (() => {
                    if (isSubmenu) {
                        return !item.icon ? { padding: "7px 5px 7px 34px" } : { padding: "10px 5px 10px 18px" };
                    }
                    return { padding: "10px 5px 10px 18px" };
                })();

                const paddedItem = {
                    ...item,
                    style: { ...(item.style || {}), ...menuPaddingStyle },
                };
                return (
                    <li
                        key={item.label || i}
                        className={classNames(styleClass, { submenuitem: isSubmenu })}
                        role="menuitem"
                    >
                        {getLink(paddedItem, i)}
                        <CSSTransition
                            nodeRef={submenuRef}
                            classNames="layout-submenu-container"
                            timeout={transitionTimeout}
                            in={
                                item.items &&
                                (
                                    // Non-collapsable menus are always expanded when they have items
                                    (item.isCollapsable === false)
                                        ? true
                                        : (props.root && !((isHorizontal() || isSlim()) && !isMobile() && (!isSlim() || (isSlim() && activeIndex !== null))) ? true : active)
                                )
                            }
                            unmountOnExit
                        >
                            <AppSubmenu
                                ref={submenuRef}
                                items={visible(item) && item.items}
                                onMenuItemClick={props.onMenuItemClick}
                                menuMode={props.menuMode}
                                menuActive={props.menuActive}
                                parentMenuItemActive={active}
                                onSubmenuToggle={props.onSubmenuToggle}
                                isSubmenu={props.root ? false : true}
                                currentPath={props.currentPath}
                            />
                        </CSSTransition>
                    </li>
                );
            }

            return null;
        });
    };

    if (!props.items) {
        return null;
    }

    const items = getItems();

    return (
        <>
            <ul ref={ref} role="menu" className={classNames(props.className, { "pt-3 pb-5": props.root })}>
                {items}
            </ul>
            {isSlim() && props.root && <Tooltip target="li:not(.active-menuitem)>.tooltip" />}
        </>
    );
});

const AppMenu = (props) => {
    const [showBottomIndicator, setShowBottomIndicator] = useState(false);
    const [showTopIndicator, setShowTopIndicator] = useState(false);
    const containerRef = useRef(null);

    const checkScroll = useCallback(() => {
        if (containerRef.current) {
            const { scrollHeight, clientHeight, scrollTop } = containerRef.current;

            // Show bottom indicator if not at bottom and content is scrollable
            // Added a larger threshold to prevent flickering
            setShowBottomIndicator(
                scrollHeight > clientHeight &&
                scrollTop + clientHeight < scrollHeight - 10
            );

            // Show top indicator if scrolled down and can scroll up
            // Added a larger threshold to prevent flickering
            setShowTopIndicator(scrollTop > 10);
        }
    }, []);

    const handleSubmenuToggle = useCallback(() => {
        // Wait for submenu animation to complete
        setTimeout(checkScroll, 300);
    }, [checkScroll]);

    useEffect(() => {
        // Initial check
        checkScroll();

        // Add scroll event listener
        const containerElement = containerRef.current;
        if (containerElement) {
            containerElement.addEventListener('scroll', checkScroll);
        }

        // Cleanup
        return () => {
            if (containerElement) {
                containerElement.removeEventListener('scroll', checkScroll);
            }
        };
    }, [checkScroll]);

    useEffect(() => {
        // Initial check after content renders
        setTimeout(checkScroll, 500);
    }, [props.model, checkScroll]);

    return (
        <>
            <div
                style={{
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                }}
            >
                {showTopIndicator && (
                    <div
                        className="menu-scroll-indicator"
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            zIndex: 1,
                            background: "linear-gradient(rgba(200, 200, 200, 0.5), transparent)",
                            // Don't block menu item clicks except on the chevron itself.
                            pointerEvents: "none",
                        }}
                    >
                        <div
                            className="w-full h-full flex justify-content-center align-items-start"
                            style={{ height: "18px", pointerEvents: "auto" }}
                        >
                            <i
                                className="pi pi-chevron-up text-500 hover:text-800 px-5 pb-1 cursor-pointer"
                                onClick={() => containerRef.current?.scrollTo({ top: 0, behavior: "smooth" })}
                            ></i>
                        </div>
                    </div>
                )}

                <div
                    ref={containerRef}
                    className="left-menu-scroll-container"
                    onScroll={checkScroll}
                >
                    <AppSubmenu
                        items={props.model}
                        className="layout-menu"
                        menuActive={props.active}
                        onRootItemClick={props.onRootMenuItemClick}
                        onMenuItemClick={props.onMenuItemClick}
                        onSubmenuToggle={handleSubmenuToggle}
                        root={true}
                        menuMode={props.menuMode}
                        parentMenuItemActive={true}
                        isSubmenu={false}
                        currentPath={props.currentPath}
                    />
                </div>

                {showBottomIndicator && (
                    <div
                        className="menu-scroll-indicator"
                        style={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            zIndex: 1,
                            background: "linear-gradient(transparent, rgba(200, 200, 200, 0.5))",
                            // Don't block menu item clicks except on the chevron itself.
                            pointerEvents: "none",
                        }}
                    >
                        <div
                            className="w-full flex justify-content-center align-items-end"
                            style={{ height: "18px", pointerEvents: "auto" }}
                        >
                            <i
                                className="pi pi-chevron-down text-500 hover:text-800 px-5 pt-1 cursor-pointer"
                                onClick={() =>
                                    containerRef.current?.scrollTo({
                                        top: containerRef.current.scrollHeight,
                                        behavior: "smooth",
                                    })
                                }
                            ></i>
                        </div>
                    </div>
                )}
            </div>
        </>
    );

};

export default AppMenu;
